import type { TFunction } from "i18next";
import { useCallback } from "react";
import type { Connection, Edge } from "@xyflow/react";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import {
  doLinkConcordanceForGraph,
  doUnlinkConcordanceForGraph,
  luHandleAppiError,
  u3141ParseGraphConcordanceMutationResponse,
  type U3141ChildLotChain,
} from "../../../../U3141Service";
import { NS } from "../../../../U3141";
import {
  u3141IsAllowedGraphConcordanceConnection,
  u3141ResolveGraphConcordanceLinkFromConnection,
  type U3141GraphConcordanceEdgeData,
} from "../../../../tools/u3141GraphConcordanceHandles";

export interface U3141UseGraphConcordanceHandlersParams {
  masterLotChainId: string;
  t: TFunction;
  toast: { success: (text: string, title?: string) => void; error: (text: string, title?: string) => void };
  applyLotChainGraphSnapshot: (graph: U3141ChildLotChain[]) => void;
  enabled: boolean;
}

export function useU3141GraphConcordanceHandlers(params: U3141UseGraphConcordanceHandlersParams) {
  const { masterLotChainId, t, toast, applyLotChainGraphSnapshot, enabled } = params;
  const axios = useU2000AxiosAuthenticated();

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
      return errorResult;
    },
    [t, toast],
  );

  const isValidConnection = useCallback(
    (connection: Edge | Connection) => {
      if (!enabled) return false;
      const source = "source" in connection ? connection.source : null;
      const target = "target" in connection ? connection.target : null;
      if (!source || !target || source === target) return false;
      return u3141IsAllowedGraphConcordanceConnection(connection.sourceHandle, connection.targetHandle);
    },
    [enabled],
  );

  const onConnect = useCallback(
    async (connection: Connection) => {
      if (!enabled) return;
      const resolved = u3141ResolveGraphConcordanceLinkFromConnection(connection);
      if (!resolved) {
        toast.error(t("errGraphConcordanceInvalidHandles", { ns: NS }));
        return;
      }
      try {
        const res = await doLinkConcordanceForGraph(axios, masterLotChainId, {
          ...resolved,
          sourceChildLotChainId: connection.source!,
          targetChildLotChainId: connection.target!,
          sourceHandle: u3141NormalizeHandleForApi(connection.sourceHandle),
          targetHandle: u3141NormalizeHandleForApi(connection.targetHandle),
        });
        const graph = u3141ParseGraphConcordanceMutationResponse(res.data);
        if (graph) {
          applyLotChainGraphSnapshot(graph);
        }
      } catch (error) {
        handleApiError(error);
      }
    },
    [enabled, axios, masterLotChainId, applyLotChainGraphSnapshot, handleApiError, t, toast],
  );

  const onEdgesDelete = useCallback(
    async (deleted: Edge[]) => {
      if (!enabled) return;
      for (const edge of deleted) {
        const meta = (edge.data as U3141GraphConcordanceEdgeData | undefined)?.u3141GraphConcordance;
        if (!meta?.concordanceHolderChildLotChainId || !meta.concordanceOriginChildLotChainId) {
          continue;
        }
        try {
          const res = await doUnlinkConcordanceForGraph(axios, masterLotChainId, {
            concordanceHolderChildLotChainId: meta.concordanceHolderChildLotChainId,
            concordanceOriginChildLotChainId: meta.concordanceOriginChildLotChainId,
          });
          const graph = u3141ParseGraphConcordanceMutationResponse(res.data);
          if (graph) {
            applyLotChainGraphSnapshot(graph);
          }
        } catch (error) {
          handleApiError(error);
        }
      }
    },
    [enabled, axios, masterLotChainId, applyLotChainGraphSnapshot, handleApiError],
  );

  return {
    isValidConnection,
    onConnect,
    onEdgesDelete,
    edgesDeletable: enabled,
    nodesConnectable: enabled,
  };
}

function u3141NormalizeHandleForApi(handle: string | null | undefined): "top" | "bottom" {
  const h = handle?.toLowerCase() ?? "";
  return h.includes("bottom") ? "bottom" : "top";
}
